import {FC, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    AutoAwesome,
    CheckCircle,
    Delete,
    Info as InfoIcon,
    PlayArrow,
} from '@mui/icons-material';
import {ImageUpload} from '../common/ImageUpload.tsx';
import {
    hasActiveThumbnailCandidate,
    useBuildThumbnailPrompt,
    useClearLibraryThumbnail,
    useCreateThumbnailCandidate,
    useDeleteThumbnailCandidate,
    useLibraryThumbnails,
    useSelectThumbnailCandidate,
} from '../../hooks/useLibraryThumbnail.ts';
import {
    BuildThumbnailPromptResponse,
    ThumbnailCandidate,
    ThumbnailCandidateStatus,
    ThumbnailMode,
    ThumbnailProvider,
} from '../../types/libraryThumbnail.ts';

interface Props {
    libraryId: number;
    currentThumbnailUrl?: string;
    onThumbnailUrlChange: (url: string | undefined) => void;
}

const STATUS_LABEL: Record<ThumbnailCandidateStatus, string> = {
    QUEUED: '대기 중',
    GENERATING: '생성 중',
    READY: '완료',
    FAILED: '실패',
    CANCELED: '취소됨',
};

const STATUS_COLOR: Record<
    ThumbnailCandidateStatus,
    'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'
> = {
    QUEUED: 'default',
    GENERATING: 'info',
    READY: 'success',
    FAILED: 'error',
    CANCELED: 'default',
};

const modeEta = (mode: ThumbnailMode): string =>
    mode === 'SYNC' ? '예상 5~15초' : '예상 1~6시간';

const ProfileLine: FC<{label: string; value: string}> = ({label, value}) => (
    <Stack direction="row" spacing={1} alignItems="flex-start">
        <Typography
            variant="caption"
            sx={{minWidth: 88, fontWeight: 600, color: 'text.secondary'}}
        >
            {label}
        </Typography>
        <Typography variant="caption" sx={{wordBreak: 'break-word'}}>
            {value}
        </Typography>
    </Stack>
);

export const LibraryThumbnailPanel: FC<Props> = ({
    libraryId,
    currentThumbnailUrl,
    onThumbnailUrlChange,
}) => {
    const [provider, setProvider] = useState<ThumbnailProvider>('GEMINI');
    const [mode, setMode] = useState<ThumbnailMode>('SYNC');
    const [prompt, setPrompt] = useState('');
    const [activeProfile, setActiveProfile] =
        useState<BuildThumbnailPromptResponse | null>(null);
    const [confirmClear, setConfirmClear] = useState(false);
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
    const [promptDialogText, setPromptDialogText] = useState<string | null>(
        null
    );

    const candidatesQuery = useLibraryThumbnails(libraryId);
    const candidates = useMemo(
        () => candidatesQuery.data ?? [],
        [candidatesQuery.data]
    );
    const hasActive = hasActiveThumbnailCandidate(candidates);

    const buildPromptMut = useBuildThumbnailPrompt(libraryId);
    const createCandidateMut = useCreateThumbnailCandidate(libraryId);
    const selectCandidateMut = useSelectThumbnailCandidate(libraryId);
    const deleteCandidateMut = useDeleteThumbnailCandidate(libraryId);
    const clearThumbnailMut = useClearLibraryThumbnail(libraryId);

    const handleBuildPrompt = async (useLlmEnhancement: boolean) => {
        try {
            const res = await buildPromptMut.mutateAsync({useLlmEnhancement});
            setPrompt(res.prompt ?? '');
            setActiveProfile(res ?? null);
        } catch (error) {
            const message =
                error instanceof Error ? error.message : '프롬프트 생성 실패';
            alert(`프롬프트 생성 실패: ${message}`);
        }
    };

    const handleCreate = async () => {
        if (!prompt.trim()) {
            alert('프롬프트를 입력하세요.');
            return;
        }
        try {
            await createCandidateMut.mutateAsync({prompt, provider, mode});
        } catch (error) {
            const message =
                error instanceof Error ? error.message : '후보 생성 실패';
            alert(`후보 생성 실패: ${message}`);
        }
    };

    const handleSelect = async (candidate: ThumbnailCandidate) => {
        try {
            await selectCandidateMut.mutateAsync(candidate.id);
            if (candidate.imageUrl) {
                onThumbnailUrlChange(candidate.imageUrl);
            }
        } catch (error) {
            const message =
                error instanceof Error ? error.message : '선택 실패';
            alert(`선택 실패: ${message}`);
        }
    };

    const handleDeleteConfirm = async () => {
        if (confirmDeleteId == null) return;
        try {
            await deleteCandidateMut.mutateAsync(confirmDeleteId);
        } catch (error) {
            const message =
                error instanceof Error ? error.message : '삭제 실패';
            alert(`삭제 실패: ${message}`);
        } finally {
            setConfirmDeleteId(null);
        }
    };

    const handleClearConfirm = async () => {
        try {
            await clearThumbnailMut.mutateAsync();
            onThumbnailUrlChange(undefined);
        } catch (error) {
            const message =
                error instanceof Error ? error.message : '비우기 실패';
            alert(`비우기 실패: ${message}`);
        } finally {
            setConfirmClear(false);
        }
    };

    return (
        <Stack spacing={2}>
            {/* 현재 썸네일 카드 */}
            <Card>
                <CardContent>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                    >
                        <Typography variant="subtitle1" fontWeight="bold">
                            현재 썸네일
                        </Typography>
                        {currentThumbnailUrl && (
                            <Button
                                size="small"
                                color="error"
                                startIcon={<Delete />}
                                onClick={() => setConfirmClear(true)}
                                disabled={clearThumbnailMut.isPending}
                            >
                                비우기
                            </Button>
                        )}
                    </Box>

                    {currentThumbnailUrl ? (
                        <Box display="flex" justifyContent="center">
                            <Box
                                component="img"
                                src={currentThumbnailUrl}
                                alt="현재 썸네일"
                                sx={{
                                    maxHeight: 200,
                                    maxWidth: '100%',
                                    objectFit: 'contain',
                                    borderRadius: 1,
                                    bgcolor: 'grey.100',
                                }}
                            />
                        </Box>
                    ) : (
                        <Alert severity="info">
                            아직 썸네일이 없습니다. AI 생성 또는 수동 업로드 후
                            후보를 선택하세요.
                        </Alert>
                    )}

                    <Divider sx={{my: 2}} />

                    <Typography variant="subtitle2" gutterBottom>
                        수동 업로드
                    </Typography>
                    <ImageUpload
                        value={currentThumbnailUrl}
                        onChange={onThumbnailUrlChange}
                        folder="library/thumbnails"
                        label=""
                        helperText="권장 크기: 800x600px, 최대 5MB"
                    />
                </CardContent>
            </Card>

            {/* AI 생성 폼 */}
            <Card>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        AI 썸네일 생성
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid size={{xs: 12, sm: 6}}>
                            <Typography variant="caption" color="text.secondary">
                                Provider
                            </Typography>
                            <ToggleButtonGroup
                                value={provider}
                                exclusive
                                size="small"
                                fullWidth
                                onChange={(_, value) => {
                                    if (value) setProvider(value);
                                }}
                            >
                                <ToggleButton value="GEMINI">GEMINI</ToggleButton>
                                <ToggleButton value="OPENAI">OPENAI</ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>
                        <Grid size={{xs: 12, sm: 6}}>
                            <Typography variant="caption" color="text.secondary">
                                Mode
                            </Typography>
                            <ToggleButtonGroup
                                value={mode}
                                exclusive
                                size="small"
                                fullWidth
                                onChange={(_, value) => {
                                    if (value) setMode(value);
                                }}
                            >
                                <ToggleButton value="SYNC">SYNC</ToggleButton>
                                <ToggleButton value="BATCH">BATCH</ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>

                        {activeProfile?.profile && (
                            <Grid size={{xs: 12}}>
                                <Card variant="outlined" sx={{bgcolor: 'grey.50'}}>
                                    <CardContent sx={{py: 1.5}}>
                                        <Stack spacing={0.75}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Chip
                                                    size="small"
                                                    color="primary"
                                                    label={activeProfile.category ?? '카테고리 미지정'}
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                    카테고리 비주얼 프로파일
                                                </Typography>
                                            </Stack>
                                            <ProfileLine label="주제" value={activeProfile.profile.subjectHint} />
                                            <ProfileLine label="팔레트" value={activeProfile.profile.palette} />
                                            <ProfileLine label="조명" value={activeProfile.profile.lighting} />
                                            <ProfileLine label="배경" value={activeProfile.profile.backdrop} />
                                            <ProfileLine label="구도" value={activeProfile.profile.composition} />
                                            <ProfileLine label="분위기" value={activeProfile.profile.moodKeywords} />
                                            <ProfileLine label="금지" value={activeProfile.profile.avoidKeywords} />
                                            {activeProfile.llmSubject && (
                                                <ProfileLine label="LLM 주제" value={activeProfile.llmSubject} />
                                            )}
                                            {activeProfile.llmMood && (
                                                <ProfileLine label="LLM 분위기" value={activeProfile.llmMood} />
                                            )}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        <Grid size={{xs: 12}}>
                            <TextField
                                label="프롬프트"
                                fullWidth
                                multiline
                                rows={6}
                                value={prompt}
                                onChange={(e) => {
                                    setPrompt(e.target.value);
                                    setActiveProfile(null);
                                }}
                                placeholder="썸네일 생성용 프롬프트를 입력하거나 아래 버튼으로 자동 생성하세요."
                            />
                        </Grid>

                        <Grid size={{xs: 12}}>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AutoAwesome />}
                                    onClick={() => handleBuildPrompt(true)}
                                    disabled={buildPromptMut.isPending}
                                >
                                    {buildPromptMut.isPending
                                        ? '생성 중...'
                                        : 'AI로 프롬프트 자동 생성'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleBuildPrompt(false)}
                                    disabled={buildPromptMut.isPending}
                                >
                                    기본 프롬프트만
                                </Button>
                                <Box flex={1} />
                                <Button
                                    variant="contained"
                                    startIcon={<PlayArrow />}
                                    onClick={handleCreate}
                                    disabled={
                                        createCandidateMut.isPending ||
                                        !prompt.trim()
                                    }
                                >
                                    {createCandidateMut.isPending
                                        ? '요청 중...'
                                        : '생성 시작'}
                                </Button>
                            </Stack>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{mt: 1, display: 'block'}}
                            >
                                SYNC: 즉시 생성 (~5-15초) / BATCH: 비동기 큐 (~1-6시간, OpenAI Batch API)
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* 후보 그리드 */}
            <Card>
                <CardContent>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                    >
                        <Typography variant="subtitle1" fontWeight="bold">
                            썸네일 후보
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {candidates.length}개
                            {hasActive && ' · 5초마다 자동 갱신 중'}
                        </Typography>
                    </Box>

                    {candidatesQuery.isLoading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress />
                        </Box>
                    ) : candidates.length === 0 ? (
                        <Alert severity="info">
                            아직 생성된 썸네일 후보가 없습니다.
                        </Alert>
                    ) : (
                        <Grid container spacing={2}>
                            {candidates.map((candidate) => (
                                <Grid
                                    key={candidate.id}
                                    size={{xs: 12, sm: 6, md: 4}}
                                >
                                    <CandidateCard
                                        candidate={candidate}
                                        onSelect={() => handleSelect(candidate)}
                                        onDelete={() =>
                                            setConfirmDeleteId(candidate.id)
                                        }
                                        onShowPrompt={() =>
                                            setPromptDialogText(candidate.prompt)
                                        }
                                        selectPending={
                                            selectCandidateMut.isPending
                                        }
                                        deletePending={
                                            deleteCandidateMut.isPending
                                        }
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </CardContent>
            </Card>

            {/* 비우기 confirm */}
            <Dialog
                open={confirmClear}
                onClose={() => setConfirmClear(false)}
            >
                <DialogTitle>썸네일 비우기</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        현재 라이브러리의 썸네일을 제거합니다. 단어장/그룹의
                        thumbnailUrl이 null로 설정됩니다. 계속하시겠습니까?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmClear(false)}>취소</Button>
                    <Button
                        color="error"
                        onClick={handleClearConfirm}
                        disabled={clearThumbnailMut.isPending}
                    >
                        비우기
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 후보 삭제 confirm */}
            <Dialog
                open={confirmDeleteId !== null}
                onClose={() => setConfirmDeleteId(null)}
            >
                <DialogTitle>후보 삭제</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        이 썸네일 후보를 삭제하시겠습니까? 이미지 파일도 함께
                        삭제됩니다.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteId(null)}>
                        취소
                    </Button>
                    <Button
                        color="error"
                        onClick={handleDeleteConfirm}
                        disabled={deleteCandidateMut.isPending}
                    >
                        삭제
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 프롬프트 보기 dialog */}
            <Dialog
                open={promptDialogText !== null}
                onClose={() => setPromptDialogText(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>프롬프트</DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body2"
                        sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}
                    >
                        {promptDialogText}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPromptDialogText(null)}>
                        닫기
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );

    function CandidateCard({
        candidate,
        onSelect,
        onDelete,
        onShowPrompt,
        selectPending,
        deletePending,
    }: {
        candidate: ThumbnailCandidate;
        onSelect: () => void;
        onDelete: () => void;
        onShowPrompt: () => void;
        selectPending: boolean;
        deletePending: boolean;
    }) {
        const status = candidate.status;
        const failed = status === 'FAILED' || status === 'CANCELED';

        return (
            <Card
                variant="outlined"
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    bgcolor: failed ? 'error.light' : undefined,
                }}
            >
                {/* 상태/선택 뱃지 + 프롬프트 i 버튼 */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        right: 8,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        zIndex: 2,
                        gap: 1,
                    }}
                >
                    <Stack direction="row" spacing={0.5}>
                        <Chip
                            size="small"
                            label={STATUS_LABEL[status]}
                            color={STATUS_COLOR[status]}
                        />
                        {candidate.isSelected && (
                            <Chip
                                size="small"
                                icon={<CheckCircle />}
                                label="사용 중"
                                color="primary"
                            />
                        )}
                    </Stack>
                    <Tooltip title="프롬프트 보기">
                        <IconButton
                            size="small"
                            onClick={onShowPrompt}
                            sx={{
                                bgcolor: 'background.paper',
                                '&:hover': {bgcolor: 'background.default'},
                            }}
                        >
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* 본문 */}
                {status === 'READY' && candidate.imageUrl ? (
                    <CardMedia
                        component="img"
                        image={candidate.imageUrl}
                        alt={`candidate-${candidate.id}`}
                        sx={{
                            aspectRatio: '2/3',
                            maxHeight: 280,
                            objectFit: 'cover',
                            bgcolor: 'grey.100',
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            aspectRatio: '2/3',
                            maxHeight: 280,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: failed ? 'error.light' : 'grey.100',
                            color: failed ? 'error.contrastText' : 'text.secondary',
                            p: 2,
                            textAlign: 'center',
                            gap: 1,
                        }}
                    >
                        {status === 'GENERATING' && <CircularProgress size={32} />}
                        <Typography variant="body2">
                            {STATUS_LABEL[status]}
                        </Typography>
                        {(status === 'QUEUED' || status === 'GENERATING') && (
                            <Typography variant="caption">
                                {modeEta(candidate.mode)}
                            </Typography>
                        )}
                        {failed && candidate.errorMessage && (
                            <Typography
                                variant="caption"
                                sx={{
                                    wordBreak: 'break-word',
                                    maxHeight: 80,
                                    overflow: 'auto',
                                }}
                            >
                                {candidate.errorMessage}
                            </Typography>
                        )}
                    </Box>
                )}

                <CardContent sx={{flex: 1, pb: '8px !important'}}>
                    <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                            {candidate.provider} · {candidate.modelName} ·{' '}
                            {candidate.mode}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {new Date(candidate.createdAt).toLocaleString()}
                        </Typography>
                    </Stack>
                </CardContent>

                <Box display="flex" gap={1} px={2} pb={2}>
                    {status === 'READY' && (
                        <Button
                            size="small"
                            variant="contained"
                            onClick={onSelect}
                            disabled={candidate.isSelected || selectPending}
                            fullWidth
                        >
                            {candidate.isSelected ? '사용 중' : '이 이미지 사용'}
                        </Button>
                    )}
                    <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={onDelete}
                        disabled={deletePending}
                    >
                        삭제
                    </Button>
                </Box>
            </Card>
        );
    }
};
